import React, { createContext, useState, useContext, useCallback, useEffect } from "react";
import TokenExpiredModal from "../components/TokenExpiredModal";
import { decodeToken } from "../utils/auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Rehydrate user from a stored token on first render.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) setUser(decoded);
    }
    setInitializing(false);
  }, []);

  // The axios interceptor fires this event when a refresh attempt fails
  // (e.g. the refresh_token cookie has expired).
  useEffect(() => {
    const handleRefreshFail = () => {
      setUser(null);
      setModalVisible(true);
    };
    window.addEventListener("auth:refresh-failed", handleRefreshFail);
    return () => window.removeEventListener("auth:refresh-failed", handleRefreshFail);
  }, []);

  const login = useCallback((token) => {
    localStorage.setItem("token", token);
    const decoded = decodeToken(token);
    console.log("Decoded token on login:", decoded);
    setUser(decoded);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const getAccessToken = useCallback(() => localStorage.getItem("token"), []);

  const handleModalLogin = () => {
    setModalVisible(false);
    window.location.href = "/login";
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        initializing,
        auth: isAuthenticated, // backward-compat alias
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
      <TokenExpiredModal visible={modalVisible} onLogin={handleModalLogin} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
